// Segundo fator: ativar, confirmar, desativar.
//
// A sequência é deliberada e está no contrato: iniciar NÃO ativa nada. A
// proteção só passa a valer depois de um código válido — senão um QR lido
// errado trancaria a pessoa fora da própria conta, e não haveria como entrar
// para desfazer.

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import * as conta from "../api/conta";
import { explicar } from "../api/problema";
import { Icone } from "../ui/Icone";
import { QR } from "./QR";

export function DoisFatores({ ativo }: { ativo: boolean }) {
  const fila = useQueryClient();
  const [codigo, setCodigo] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [recuperacao, setRecuperacao] = useState<string[] | null>(null);

  const iniciar = useMutation({
    mutationFn: () => conta.iniciar2FA(),
    onError: (e) => setErro(explicar(e)),
  });

  const confirmar = useMutation({
    mutationFn: () => conta.confirmar2FA(codigo),
    onSuccess: (d) => {
      setErro("");
      setRecuperacao(d.codigos_recuperacao ?? []);
      iniciar.reset();
      void fila.invalidateQueries({ queryKey: ["conta"] });
    },
    onError: (e) => setErro(explicar(e)),
  });

  const desativar = useMutation({
    mutationFn: () => conta.desativar2FA(senha),
    onSuccess: () => {
      setSenha("");
      setErro("");
      void fila.invalidateQueries({ queryKey: ["conta"] });
    },
    onError: (e) => setErro(explicar(e)),
  });

  // Os códigos de recuperação aparecem UMA vez. Depois só existe o hash.
  if (recuperacao) {
    return (
      <Secao titulo="Guarde os códigos de recuperação" icone="KeyRound">
        <Aviso tom="aviso">
          Esta é a <strong>única vez</strong> que eles aparecem. Cada um serve
          uma vez, e são o que devolve o acesso se você perder o celular.
          Imprima ou guarde num gerenciador de senhas.
        </Aviso>
        <ul className="my-4 grid grid-cols-2 gap-2 font-mono text-sm">
          {recuperacao.map((c) => (
            <li key={c} className="rounded border border-linha bg-fundo px-3 py-2 text-center">
              {c}
            </li>
          ))}
        </ul>
        <button
          onClick={() => setRecuperacao(null)}
          className="rounded-lg bg-acao px-4 py-2 text-sm text-white"
        >
          Guardei os códigos
        </button>
      </Secao>
    );
  }

  if (ativo) {
    return (
      <Secao titulo="Verificação em duas etapas" icone="ShieldCheck" estado="ativa">
        <p className="text-sm text-tinta-media">
          Sua conta pede um código do aplicativo autenticador ao entrar.
        </p>
        <Aviso tom="neutro">
          Clientes de e-mail — Thunderbird, Outlook, o app do celular — não têm
          como apresentar o segundo fator. Eles usam{" "}
          <strong>senhas de aplicativo</strong>, abaixo.
        </Aviso>

        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-perigo">Desativar</summary>
          <p className="mt-2 text-sm text-tinta-media">
            Pedimos sua senha atual: uma sessão roubada não pode desligar a
            proteção que existe justamente para o caso de a senha ter vazado.
          </p>
          <div className="mt-3 flex gap-2">
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="Senha atual"
              autoComplete="current-password"
              className="flex-1 rounded-lg border border-linha px-3 py-2 text-sm"
            />
            <button
              onClick={() => desativar.mutate()}
              disabled={!senha || desativar.isPending}
              className="rounded-lg bg-perigo px-4 py-2 text-sm text-white disabled:opacity-60"
            >
              Desativar
            </button>
          </div>
        </details>
        {erro && <p className="mt-3 text-sm text-perigo">{erro}</p>}
      </Secao>
    );
  }

  return (
    <Secao titulo="Verificação em duas etapas" icone="ShieldAlert">
      {!iniciar.data ? (
        <>
          <p className="text-sm text-tinta-media">
            Com ela ligada, quem tiver sua senha ainda não entra: falta o código
            que só o seu celular gera.
          </p>
          <button
            onClick={() => iniciar.mutate()}
            disabled={iniciar.isPending}
            className="mt-4 rounded-lg bg-acao px-4 py-2 text-sm text-white disabled:opacity-60"
          >
            {iniciar.isPending ? "…" : "Ativar"}
          </button>
        </>
      ) : (
        <>
          {!!iniciar.data.senhas_app_ativas && (
            <Aviso tom="aviso">
              Você tem <strong>{iniciar.data.senhas_app_ativas}</strong> senha(s)
              de aplicativo em uso. Elas continuam valendo — mas, daqui em
              diante, é só por elas que Thunderbird, Outlook e o celular
              entram.
            </Aviso>
          )}

          <div className="my-4 flex flex-wrap items-start gap-6">
            <div className="rounded-lg border border-linha p-3">
              <QR uri={iniciar.data.uri ?? ""} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <ol className="list-decimal space-y-2 pl-5 text-sm text-tinta-media">
                <li>Abra o aplicativo autenticador no celular.</li>
                <li>Aponte para o QR, ou digite o código abaixo.</li>
                <li>Informe os seis dígitos que ele mostrar.</li>
              </ol>
              <p className="mt-3 break-all rounded border border-linha bg-fundo px-3 py-2 font-mono text-xs">
                {iniciar.data.segredo}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <label htmlFor="codigo-2fa" className="sr-only">
              Código de seis dígitos
            </label>
            <input
              id="codigo-2fa"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              className="w-36 rounded-lg border border-linha px-3 py-2 text-sm tracking-[0.3em]"
            />
            <button
              onClick={() => confirmar.mutate()}
              disabled={codigo.length < 6 || confirmar.isPending}
              className="rounded-lg bg-acao px-4 py-2 text-sm text-white disabled:opacity-60"
            >
              Confirmar
            </button>
          </div>
        </>
      )}
      {erro && <p className="mt-3 text-sm text-perigo">{erro}</p>}
    </Secao>
  );
}

export function Secao({
  titulo,
  icone,
  estado,
  naoSalvo,
  children,
}: {
  titulo: string;
  icone: Parameters<typeof Icone>[0]["nome"];
  estado?: string;
  /**
   * Há alteração digitada e não guardada nesta seção.
   *
   * Apontamento 14: com um Salvar por seção, é fácil mexer em duas e salvar
   * uma. O selo fica no TÍTULO, que é o que a pessoa vê ao rolar — e a borda
   * muda de cor, para a seção se destacar mesmo com o título fora da tela.
   */
  naoSalvo?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section
      className={`rounded-xl border bg-white p-6 ${
        naoSalvo ? "border-aviso" : "border-linha"
      }`}
    >
      <h2 className="mb-4 flex items-center gap-2 font-semibold text-tinta">
        <Icone nome={icone} tamanho={18} className="text-acao" />
        {titulo}
        {estado && (
          <span className="rounded-full bg-acao-clara px-2 py-0.5 text-xs font-normal text-acao-escura">
            {estado}
          </span>
        )}
        {naoSalvo && (
          <span className="rounded-full bg-aviso/15 px-2 py-0.5 text-xs font-normal text-aviso">
            não salvo
          </span>
        )}
      </h2>
      {children}
    </section>
  );
}

export function Aviso({ tom, children }: { tom: "aviso" | "neutro"; children: React.ReactNode }) {
  const cor =
    tom === "aviso" ? "border-aviso/40 bg-aviso/10" : "border-linha bg-fundo";
  return (
    <div className={`mt-4 flex gap-3 rounded-lg border ${cor} px-3 py-2 text-sm text-tinta`}>
      <Icone nome="Info" tamanho={16} className="mt-0.5 flex-shrink-0 text-tinta-fraca" />
      <div>{children}</div>
    </div>
  );
}
